'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import Image from 'next/image';
import { FC, useLayoutEffect, useRef } from 'react';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useCustomCursor } from '@/hooks/useCustomCursor';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import CharityCard from './CharityCard';
import css from './style.module.scss';
import { CharityDto } from './types';
interface Props {
  data: CharityDto;
  htmlId?: string;
}
const Charity: FC<Props> = t0 => {
  const $ = _c(32);
  const {
    data,
    htmlId
  } = t0;
  const device = useDevice();
  const {
    setCursorVariant,
    setIsCursorVisible
  } = useCustomCursor();
  const tl = useRef(null);
  const wrapperRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const wrapper = wrapperRef.current;
      const graffiti = wrapper?.querySelector(`.${css.graffitiBg}`);
      const titleLines = wrapper?.querySelectorAll(`.${css.title} p`);
      const circle = wrapper?.querySelector(`.${css.curvedText}`);
      const chub = wrapper?.querySelector(`.${css.chub}`);
      const cards = wrapper?.querySelectorAll(`.${css.card}`);
      if (!wrapper || !circle || !titleLines || !graffiti || !chub || !cards?.length) {
        return;
      }
      const ctx = gsap.context(() => {
        const matchMedia = gsap.matchMedia();
        matchMedia.add({
          isDesktop: "(min-width: 992px)",
          isMobile: "(max-width: 991px)"
        }, context => {
          const {
            isMobile
          } = context.conditions as gsap.Conditions;
          gsap.set(titleLines, {
            opacity: 0,
            y: 40,
            filter: "blur(10px)"
          });
          gsap.to(titleLines, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1.1,
            ease: "power3.out",
            stagger: 0.15,
            scrollTrigger: {
              trigger: titleLines,
              start: "top 80%",
              toggleActions: "play none none reverse"
            }
          });
          tl.current = gsap.timeline({
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: wrapper,
              start: "+=1",
              end: "bottom",
              onUpdate: t3 => {
                const {
                  progress
                } = t3;
                const scrollProgress = progress * 360;
                gsap.to(circle, {
                  rotate: scrollProgress,
                  ease: "none",
                  duration: 0
                });
              }
            }
          });
          if (isMobile) {
            return;
          }
          gsap.to(graffiti, {
            yPercent: -30,
            ease: "expo.out",
            opacity: 1,
            scrollTrigger: {
              trigger: wrapper,
              start: "top+=1px bottom",
              end: "bottom",
              scrub: true
            }
          });
          gsap.to(chub, {
            yPercent: 30,
            ease: "none",
            scrollTrigger: {
              trigger: wrapper,
              start: "top bottom",
              end: "bottom top",
              scrub: true
            }
          });
          cards.forEach((card, i) => {
            gsap.to(card, {
              yPercent: i === 0 ? 60 : i === 1 ? -70 : 30,
              ease: "none",
              scrollTrigger: {
                trigger: wrapper,
                start: "top bottom",
                end: "bottom top",
                scrub: true
              }
            });
          });
        });
      }, wrapper);
      return () => {
        ctx.revert();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clsx("wrapper", css.wrapper);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== setCursorVariant) {
    t4 = () => setCursorVariant("lottie-green-visit-case");
    $[3] = setCursorVariant;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image className={css.chub} width={600} height={470} src="/assets/images/about-us-page/charity/chub.webp" alt="" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== data.title) {
    t6 = <Typography uppercase={true} className={css.title} variant="h2" tag="h2"><StrapiRichContent content={data.title} /></Typography>;
    $[6] = data.title;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Image className={css.graffitiBg} width={500} height={500} src="/assets/images/graffiti-service.svg" alt="" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== data.cards || $[10] !== setIsCursorVisible) {
    let t9;
    if ($[12] !== setIsCursorVisible) {
      t9 = (card_0, index) => <CharityCard key={index} className={css.card} data={card_0} onMouseEnter={() => setIsCursorVisible(true)} onMouseLeave={() => setIsCursorVisible(false)} />;
      $[12] = setIsCursorVisible;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    t8 = data.cards.map(t9);
    $[9] = data.cards;
    $[10] = setIsCursorVisible;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className={css.cards}>{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t6 || $[18] !== t9) {
    t10 = <div className={css.contentWrapper} onMouseEnter={t4}>{t5}{t6}{t7}{t9}</div>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className={css.curvedContainer}><div><Image width={1775} height={1775} src="/assets/images/circle-mask.svg" alt="" className={css.curvedMask} /></div></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = device === "desktop" ? 1775 : 424;
  const t13 = device === "desktop" ? 1775 : 424;
  const t14 = device === "desktop" ? "/assets/images/circle-text-desk.webp" : "/assets/images/circle-text-mobile.webp";
  let t15;
  if ($[21] !== t12 || $[22] !== t13 || $[23] !== t14) {
    t15 = <div className={css.curvedContainer}><Image width={t12} height={t13} alt="" className={css.curvedText} src={t14} /></div>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className={css.bottomClearFix} />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15) {
    t17 = <div className={css.curvedWrapper}>{t11}{t15}{t16}</div>;
    $[26] = t15;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== htmlId || $[29] !== t10 || $[30] !== t17) {
    t18 = <section ref={wrapperRef} id={htmlId} className={t3}>{t10}{t17}</section>;
    $[28] = htmlId;
    $[29] = t10;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  return t18;
};
export default Charity;